import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, Mic, Plus, Square, X } from "lucide-react";
import useCortiDictation from "../../hooks/useCortiDictation";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";
import PulmonologyMotifChips from "../PulmonologyMotifChips/PulmonologyMotifChips";
import "./ReasonAndSymptomsSection.css";

const symptoms = [
  "chronicCough", "dyspnea", "wheezing", "chestPain", "hemoptysis", "sputum",
  "osa", "abnormalImaging", "preoperative", "followUp",
];

export default function ReasonAndSymptomsSection({ patientId, record, saveRecord, saving, canSave }) {
  const { t, i18n } = useTranslation("pulmonology");
  const [selected, setSelected] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [mMrc, setMMrc] = useState(0);
  const [editingComplaintId, setEditingComplaintId] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [savedNarratives, setSavedNarratives] = useState([]);
  const [narrativeSaved, setNarrativeSaved] = useState(false);
  const [editingNarrativeId, setEditingNarrativeId] = useState(null);
  const [narrativeEditDraft, setNarrativeEditDraft] = useState("");
  const nextComplaintId = useRef(1);
  const nextNarrativeId = useRef(1);
  const { dictationRef, status: dictationStatus, error: dictationError, toggleDictation } = useCortiDictation({
    language: i18n.resolvedLanguage || i18n.language,
    onTranscript: (text) => {
      setNarrative((current) => `${current}${current && !/\s$/.test(current) ? " " : ""}${text}`);
      setNarrativeSaved(false);
    },
  });
  const isDictationRecording = dictationStatus === "recording";
  const isDictationConnecting = dictationStatus === "connecting";
  const persistedRecord = Array.isArray(record) ? record[0] : record;

  useEffect(() => {
    if (!persistedRecord) return;
    setSelected(persistedRecord.reasons || []);
    setComplaints((persistedRecord.symptoms || []).map((symptom, index) => ({
      id: index + 1,
      name: symptom.name,
      duration: symptom.duration || "",
      severity: symptom.severity || "",
      trigger: symptom.triggerContext || "",
      saved: true,
    })));
    setNarrative(persistedRecord.symptomNarrative || "");
    setMMrc(persistedRecord.mmrcDyspneaScale ?? 2);
  }, [persistedRecord]);

  const saveSymptoms = () => saveRecord?.("reason-symptoms", {
    reasons: selected,
    symptoms: complaints.filter((item) => item.name.trim()).map(({ name, duration, severity, trigger }) => ({
      name,
      duration,
      severity,
      triggerContext: trigger,
    })),
    symptomNarrative: [narrative, ...savedNarratives.map((item) => item.text)].filter(Boolean).join("\n"),
    mmrcDyspneaScale: mMrc,
  }, persistedRecord?.id);

  const updateComplaint = (id, field, value) => {
    setComplaints((items) => items.map((item) => item.id === id
      ? { ...item, [field]: value, saved: false }
      : item));
  };

  const saveComplaint = (event, id) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    setComplaints((items) => items.map((item) => item.id === id ? { ...item, saved: true } : item));
    setEditingComplaintId(null);
  };

  const addComplaint = () => {
    const id = nextComplaintId.current++;
    setComplaints((items) => [...items, {
      id,
      name: "",
      duration: "",
      severity: "",
      trigger: "",
      saved: false,
    }]);
    setEditingComplaintId(id);
  };

  return (
    <div className="pulmo-form">
      <div className="pulmo-subhead">
        <strong>{t("reason.title")}</strong>
        <span className="pulmo-sync">{persistedRecord ? t("reason.synced") : t("common.notRecorded")}</span>
      </div>
      <PulmonologyMotifChips
        key={`${patientId}-reason-symptoms`}
        patientId={patientId}
        group="reason-symptoms"
        defaultOptions={symptoms}
        selected={selected}
        labelForOption={(option) => symptoms.includes(option) ? t(`reason.symptoms.${option}`) : option}
        onToggle={(option) => setSelected((items) => items.includes(option) ? items.filter((item) => item !== option) : [...items, option])}
        onRemove={(option) => setSelected((items) => items.filter((item) => item !== option))}
      />
      <strong className="pulmo-subtitle">{t("reason.symptomsTitle")}</strong>
      <div className="pulmo-complaints">
        {complaints.map((complaint) => (
          <div className="pulmo-complaint" key={complaint.id}>
            {complaint.saved && editingComplaintId !== complaint.id
              ? <button type="button" className="pulmo-complaint__display" aria-label={t("reason.editComplaint", { name: complaint.name })} onClick={() => setEditingComplaintId(complaint.id)}>
                  <strong>{complaint.name}</strong>
                  <span>{complaint.duration}</span>
                  <span>{t("reason.severity")} <b>{complaint.severity}</b></span>
                  <span>{complaint.trigger}</span>
                </button>
              : <div className="pulmo-complaint__fields">
                  <input autoFocus={editingComplaintId === complaint.id} value={complaint.name} aria-label={t("reason.fields.symptom")} placeholder={t("reason.fields.symptom")} onChange={(event) => updateComplaint(complaint.id, "name", event.target.value)} onKeyDown={(event) => saveComplaint(event, complaint.id)} />
                  <input value={complaint.duration} aria-label={t("reason.fields.duration")} placeholder={t("reason.fields.duration")} onChange={(event) => updateComplaint(complaint.id, "duration", event.target.value)} onKeyDown={(event) => saveComplaint(event, complaint.id)} />
                  <input value={complaint.severity} aria-label={t("reason.fields.severity")} placeholder={t("reason.fields.severity")} onChange={(event) => updateComplaint(complaint.id, "severity", event.target.value)} onKeyDown={(event) => saveComplaint(event, complaint.id)} />
                  <input value={complaint.trigger} aria-label={t("reason.fields.trigger")} placeholder={t("reason.fields.trigger")} onChange={(event) => updateComplaint(complaint.id, "trigger", event.target.value)} onKeyDown={(event) => saveComplaint(event, complaint.id)} />
                </div>}
            <div className="pulmo-complaint__actions">
              <button type="button" className="is-delete" aria-label={t("reason.deleteComplaint")} title={t("reason.deleteComplaint")} onClick={() => setComplaints((items) => items.filter((item) => item.id !== complaint.id))}><X size={15} /></button>
            </div>
          </div>
        ))}
        {savedNarratives.map((entry) => (
          <div className="pulmo-complaint pulmo-narrative-result" key={entry.id}>
            {editingNarrativeId === entry.id
              ? <input
                  autoFocus
                  className="pulmo-narrative-result__input"
                  aria-label={t("reason.editComplaint", { name: t("reason.narrative") })}
                  value={narrativeEditDraft}
                  onChange={(event) => setNarrativeEditDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (narrativeEditDraft.trim()) {
                        setSavedNarratives((items) => items.map((item) => item.id === entry.id
                          ? { ...item, text: narrativeEditDraft.trim() }
                          : item));
                      }
                      setEditingNarrativeId(null);
                    } else if (event.key === "Escape") {
                      setEditingNarrativeId(null);
                    }
                  }}
                />
              : <button
                  type="button"
                  className="pulmo-narrative-result__text"
                  aria-label={t("reason.editComplaint", { name: t("reason.narrative") })}
                  onClick={() => {
                    setNarrativeEditDraft(entry.text);
                    setEditingNarrativeId(entry.id);
                  }}
                >{entry.text}</button>}
            <div className="pulmo-complaint__actions">
              <button type="button" className="is-delete" aria-label={t("reason.deleteComplaint")} title={t("reason.deleteComplaint")} onClick={() => {
                setSavedNarratives((items) => items.filter((item) => item.id !== entry.id));
                setEditingNarrativeId((current) => current === entry.id ? null : current);
              }}><X size={15} /></button>
            </div>
          </div>
        ))}
        <button type="button" className="pulmo-add-row" onClick={addComplaint}>
          <Plus size={12} /> {t("reason.addComplaint")}
        </button>
      </div>
      <div className="pulmo-symptom-summary">
        <div>
          <strong>{t("reason.scale")}</strong>
          <div className="pulmo-segmented" role="group" aria-label={t("reason.scale")}>
            {[0, 1, 2, 3, 4].map((score) => <button type="button" key={score} className={mMrc === score ? "is-selected" : ""} aria-pressed={mMrc === score} onClick={() => setMMrc(score)}>{score}</button>)}
          </div>
          <small>{t("reason.scaleDescription")}</small>
        </div>
      </div>
      <div className="pulmo-narrative">
        <div className="pulmo-field-label-row">
          <label className="pulmo-field-label" htmlFor="pulmo-symptom-summary">{t("reason.narrative")}</label>
          <div className="pulmo-dictation-controls">
            <span>{t("reason.autoDictate")}</span>
            <span className={`pulmo-dictation-switch${isDictationRecording ? " is-on" : ""}${isDictationConnecting ? " is-connecting" : ""}`} aria-hidden="true"><span /></span>
            <button type="button" className={`pulmo-dictation-mic${isDictationRecording ? " is-recording" : ""}${isDictationConnecting ? " is-connecting" : ""}`} aria-label={isDictationRecording || isDictationConnecting ? t("reason.stopDictation") : t("reason.startDictation")} aria-pressed={isDictationRecording} onClick={toggleDictation}>
              {isDictationConnecting ? <Loader2 size={14} /> : isDictationRecording ? <Square size={12} /> : <Mic size={14} />}
            </button>
          </div>
        </div>
        <textarea id="pulmo-symptom-summary" value={narrative} onChange={(event) => {
          setNarrative(event.target.value);
          setNarrativeSaved(false);
        }} placeholder={t("reason.narrativePlaceholder")} />
        <div className="pulmo-narrative__footer">
          {narrativeSaved && <span className="pulmo-complaint__saved"><Check size={12} /> {t("reason.saved")}</span>}
          <button type="button" className="pulmo-narrative__add" onClick={() => {
            if (!narrative.trim()) return;
            setSavedNarratives((items) => [...items, {
              id: nextNarrativeId.current++,
              text: narrative.trim(),
            }]);
            setNarrative("");
            setNarrativeSaved(true);
          }}>
            {narrativeSaved ? <Check size={13} /> : <Plus size={13} />}
            {narrativeSaved ? t("reason.saved") : t("reason.addNarrative")}
          </button>
        </div>
        {dictationError && <span className="pulmo-dictation-error" role="status">{dictationError}</span>}
      </div>
      <div className="pulmo-actions">
        <PulmonologySaveButton onClick={saveSymptoms} saving={saving} disabled={!canSave} />
      </div>
      <corti-dictation ref={dictationRef} aria-hidden="true" className="pulmo-hidden-dictation" />
    </div>
  );
}
